import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { QuickEditService } from './quick-edit.service';
import { ImageMapsService } from '../../map-edit/commands/image-maps.service';
import { SmService } from 'supermappe-core';
import { MathContent, MathContentMessage, MathImage, SpeakMessage } from '../../../../../commondata/messages/messages-dto';
import { ModuleStateService } from '../../module-state.service';

import { mathjax } from 'mathjax-full/js/mathjax.js';
import { TeX } from 'mathjax-full/js/input/tex.js';
import { SVG } from 'mathjax-full/js/output/svg.js';
import { liteAdaptor } from 'mathjax-full/js/adaptors/liteAdaptor.js';
import { RegisterHTMLHandler } from 'mathjax-full/js/handlers/html.js';
import { AllPackages } from 'mathjax-full/js/input/tex/AllPackages.js';

import * as canvg from 'canvg';
import {MathStringsUtils} from "../../../../../commondata/scripts/math-strings-utils";


@Injectable({
    providedIn: 'root'
})
export class MapsMathService implements OnDestroy {

    public mathToggleRequest = new EventEmitter<boolean>();
    public onErrorImageEmitter = new EventEmitter();

    private isMathEnabled: boolean = false;
    public isMathOpen: boolean = false;

    private onMathContentReceived: Subscription | undefined;
    private onMathOpenReceived: Subscription | undefined;

    private mathJaxAdaptor: any;
    private mathJaxTex: any;
    private mathJaxSvg: any;
    private mathJaxHtml: any;

    public latexPlaceholderImageSymbol = "□";

  public mathStringsUtilsService: MathStringsUtils = new MathStringsUtils();

    constructor(
        private quickEditService: QuickEditService,
        private smService: SmService,
        private imageMapsService: ImageMapsService,
        private moduleStateService: ModuleStateService,
    ) {
        // Compute Engine
        // const ce = (window as any).ce;
        (window as any).mathService = this;
        this.initMathJax();
        this.registerEvents();
    }

    private initMathJax() {
        this.mathJaxAdaptor = liteAdaptor();
        RegisterHTMLHandler(this.mathJaxAdaptor);

        this.mathJaxTex = new TeX({
            packages: AllPackages,
        });
        this.mathJaxSvg = new SVG({
            fontCache: 'local',
        });
        this.mathJaxHtml = mathjax.document('', {
            InputJax: this.mathJaxTex,
            OutputJax: this.mathJaxSvg
        });
        // this.convertLatexToBase64('M', 20).then((img: MathImage) => {
        //     if (img.width > 0 && img.height > 0) {
        //         this.refWidth = img.width;
        //         this.refHeight = img.height;
        //     }
        // }).catch((error) => {
        //     console.log('Initialize MathJax ref char error')
        //     console.error(error);
        // });
    }

    private registerEvents() {
        this.onMathContentReceived = this.moduleStateService.onMathContent.subscribe((msg: MathContentMessage) => {
            if (msg.msgData.mathContent[0].isError) {
                this.onErrorImageEmitter.emit(msg.msgData);
                return;
            }
            //manage math content
            this.manageMathContentMessage(msg);
        });

        this.onMathOpenReceived = this.moduleStateService.onMathOpen.subscribe((open: any) => {
            this.isMathOpen = open.mathOpen;

            if (this.isMathOpen) {
                this.quickEditService.disableAll();
            }
            this.smService.setEnableKeyPresses(!this.isMathOpen);
        });
    }

    ngOnDestroy(): void {
        if (this.onMathContentReceived) this.onMathContentReceived.unsubscribe();
        if (this.onMathOpenReceived) this.onMathOpenReceived.unsubscribe();
    }

    init(isMathEnabled: boolean): void {
        this.isMathEnabled = isMathEnabled;
    }

    //Apre l'editor di matematica a partire dal nodo selezionato
    public editMath() {
        if (this.isMathEnabled) {
            let latex: string | null = '';
            const node = this.smService.getSelectedNode();
            if (node) {
                latex = this.getLatexFromNode(node);
            }
            if (latex != null) {
                this.setLatexInEditor(latex);
            }
        }
    }

    //Ritorna il latex di un nodo
    public getLatexFromNode(node: any): string | null {
        if (!node) return null;
        let latex = '';
        if (this.isMathEnabled) {
            const mathLinkIndex = node.getMathLinkIndex();
            if (mathLinkIndex >= 0 && mathLinkIndex < node.links.length) {
                const mathLink = node.links[mathLinkIndex];
                if (mathLink) {
                    latex = mathLink.uri;
                    latex = latex.trim();
                }
            }
        }
        return latex;
    }

    //Apre editor di matematica con il latex come parametro
    public setLatexInEditor(latex: string, openKeyboard: boolean = true) {
        this.sendMessage(new MathContentMessage(this.moduleStateService.guid, [new MathContent(latex, "", null, "")], openKeyboard));
    }

    public sendMessage(message: any) {
        (window as any).top.postMessage(message, "*");
    }

    private async manageMathContentMessage(msg: MathContentMessage) {
        if (this.isMathEnabled) {
            const nElem = msg.msgData.mathContent.length;
            if (nElem === 1) {
                const node = this.smService.getSelectedNode();
                await this.commitMathContentToNodePromise(msg.msgData.mathContent[0], node);
            } else {
                this.smService.resetSelection();
                for (let i = 0; i < nElem; i++) {
                    const node = this.smService.addNodeAndSelectIfNoSelection('');
                    this.smService.selectNode(node);
                    await this.commitMathContentToNodePromise(msg.msgData.mathContent[i], node);
                }
            }
        }
    }

    private commitMathContentToNodePromise(msgData: any, editNode: any) {
        return new Promise((resolve, reject) => {
            //if no node is selected, create a new one
            if (!editNode) {
                console.log('+++LATEX CHANGED');
                this.smService.addNodeAndSelectIfNoSelection('');
                editNode = this.smService.getSelectedNode();
            }
            if (editNode) {
                if (msgData.mathLatex !== '') {
                    if (msgData.mathLatex !== this.getLatexFromNode(editNode)) {
                        console.log('+++LATEX CHANGED');
                        const dataUrl = msgData.mathImage.base64;
                        if (dataUrl) {
                            //console.log(dataUrl)
                            console.log('+++UPDATIG LATEX IMAGE');
                            this.smService.setImagePlaceholderToNodeId(editNode.id, true);
                            this.smService.setLatexToNode(editNode.id, msgData.mathLatex);
                            this.imageMapsService.insertImageFromUri(dataUrl, "", editNode.id, true, (success, data) => {
                                if (success) {
                                    console.log('+++LATEX IMAGE INSERTED');
                                } else {
                                    console.log('+++LATEX IMAGE NOT INSERTED');
                                }
                                this.smService.setImagePlaceholderToNodeId(editNode.id, false);
                                return resolve(true);
                            });
                        } else {
                            console.log('+++LATEX IMAGE NOT FOUND');
                            return resolve(false);
                        }
                    } else {
                        console.log('+++LATEX NOT CHANGED');
                        return resolve(false);
                    }
                } else {
                    console.log('+++LATEX EMPTY REMOVING MATH CONTENT');
                    this.smService.setLatexToNode(editNode.id, '');
                    this.smService.removeImageFromSelection();
                    return resolve(false);
                }
            } else {
                console.log('+++NO NODE');
                return resolve(false);
            }
        });
    }

    async convertLatexToBase64(latex: string): Promise<MathImage> {
        const svgScaleFactor = 10;
        const svgToCanvasScaleFactor = 5;

        latex = latex.replace(/\\placeholder\b/g, this.latexPlaceholderImageSymbol);

        //converte il latex con mathjax
        const svg = await mathjax.handleRetriesFor(() => {
            return this.mathJaxHtml.convert(latex, { display: true }); //this.mathJaxHtml.convert(latex, { display: true });
        }).then((node: any) => {
            const svg = this.mathJaxAdaptor.innerHTML(node).match(/<svg[^>]*>[\s\S]*<\/svg>/)?.[0];
            if (!svg) {
                return MathImage.error('Errore: impossibile generare SVG da MathJax con latex.');
            }
            return svg;
        }).catch((error: any) => {
            return MathImage.error('Errore: impossibile generare SVG da MathJax con latex');
        });

        //Gestione dimensioni svg
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svg, 'image/svg+xml');
        const svgElement = svgDoc.documentElement;

        //dimensioni svg
        const svgWidth = parseInt(svgElement.getAttribute('width') || '0', 10) * svgScaleFactor;
        const svgHeight = parseInt(svgElement.getAttribute('height') || '0', 10) * svgScaleFactor;

        if (svgWidth === 0 || svgHeight === 0) {
            return MathImage.error('Errore: dimensioni SVG non valide.');
        }

        // // calcolo del fattore scala
        // let scaleFactor = 1;
        // if (svgWidth > svgHeight) {
        //     scaleFactor = dimension / svgWidth;
        // } else {
        //     scaleFactor = dimension / svgHeight;
        // }

        const canvasWidth = svgWidth * svgToCanvasScaleFactor;
        const canvasHeight = svgHeight * svgToCanvasScaleFactor;

        //Crea un canvas e contesto
        const canvas = new OffscreenCanvas(canvasWidth, canvasHeight);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return MathImage.error('Errore: impossibile ottenere il contesto del canvas.');
        }

        try {

            //Usa Canvg per renderizzare l'SVG nel canvas
            const v = await canvg.Canvg.from(ctx, svg);
            //resize dell'svg nel canvas
            v.resize(canvasWidth, canvasHeight, true);
            await v.render();

            //Converti il canvas in PNG Base64
            const blob = await canvas.convertToBlob();
            // Restituisce il base64 con async/await per una gestione più semplice
            const base64 = await new Promise<string>((resolve, reject) => {
                const blobReader = new FileReader();
                blobReader.onloadend = () => {
                    if (blobReader.result) {
                        resolve(blobReader.result as string); // Restituisce il base64
                    } else {
                        reject('Errore nella lettura del blob');
                    }
                };
                blobReader.onerror = () => reject('Errore nella lettura del blob');

                //lettura del blob
                blobReader.readAsDataURL(blob);
            });

            return MathImage.fromBase64(base64, svgWidth, svgHeight);

        } catch (error) {
            console.error('Errore nella conversione:', error);
            return MathImage.error('Errore durante la conversione del LaTeX in immagine');
        } finally {
            // Pulizia
            canvas.width = 0;
            canvas.height = 0;
        }
    }


}
